import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { FONT_MAIN, glassCard, inputClass, inputStyle } from "./MobileFamiliares";
import { ArrowLeft, Ban, Car, CheckCircle2, ChevronDown, Copy, HelpCircle, Link2, Loader2, Mail, MailWarning, MessageCircle, RefreshCw, Send, X } from "lucide-react";

const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmtData = (iso) => {
  if (!iso) return "—";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${String(a).slice(2)}`;
};

const agoraHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const textoConvite = (c, condo, eql) => {
  const janela = `${fmtData(c.data_inicial)}${c.hora_inicial ? ` ${c.hora_inicial}` : ""} → ${fmtData(c.data_final)}${c.hora_final ? ` ${c.hora_final}` : ""}`;
  const passoCpf = c.modo !== "automatico"
    ? "\n3) Informe seu CPF para confirmar o check-in;\n4) Pronto — o acesso libera na hora."
    : "\n3) Pronto — o acesso libera na hora.";
  const veiculo = [c.visitante_placa, c.visitante_modelo, c.visitante_cor].filter(Boolean).join(" · ");
  const linhaUnidade = eql ? `\nUnidade: ${eql}` : "";
  const linhaVeiculo = veiculo ? `\nVeículo: ${veiculo}` : "";
  return `Olá${c.visitante_nome ? `, ${c.visitante_nome}` : ""}! Você recebeu um convite de acesso a(o) ${condo || "condomínio"}.${linhaUnidade}${linhaVeiculo}\nValidade: ${janela}\n\nComo usar:\n1) Abra o link do convite no seu celular;\n2) Ao chegar, leia o QR Code fixado no portão/porta;${passoCpf}\n\nLink do convite:\n${c.link_pwa} \r\n`;
};

const cpfValido = (cpf) => {
  const d = cpf.replace(/\D/g, "");
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const dig = (base) => {
    let s = 0;
    for (let i = 0; i < base.length; i++) s += Number(base[i]) * (base.length + 1 - i);
    const r = (s * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return dig(d.slice(0, 9)) === Number(d[9]) && dig(d.slice(0, 10)) === Number(d[10]);
};

const fmtDDI = (v) => {
  let d = v.replace(/[^\d+]/g, "");
  if (!d.startsWith("+")) d = `+${d.replace(/\+/g, "")}`;
  return `+${d.slice(1, 4)}`;
};

const fmtFoneBR = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (!d) return "";
  const ddd = d.slice(0, 2);
  const resto = d.slice(2);
  const tam1 = resto.length > 8 ? 5 : 4;
  let out = `(${ddd}`;
  if (ddd.length === 2) out += ")";
  if (resto) out += ` ${resto.slice(0, tam1)}`;
  if (resto.length > tam1) out += `-${resto.slice(tam1)}`;
  return out;
};

const zapUrl = (digits, c, condo, eql) => {
  let d = digits.replace(/\D/g, "");
  if (d.length <= 11) d = `55${d}`;
  return `https://wa.me/${d}?text=${encodeURIComponent(textoConvite(c, condo, eql))}`;
};

const mailtoLink = (para, c, condo, eql) =>
  `mailto:${encodeURIComponent(para)}?subject=${encodeURIComponent("Convite de acesso ao condomínio")}&body=${encodeURIComponent(textoConvite(c, condo, eql))}`;

const STATUS_PILL = {
  ativo: { label: "Ativo", color: "#7ef0a0", bg: "rgba(34,197,94,0.12)" },
  expirado: { label: "Expirado", color: "#8d90a0", bg: "rgba(255,255,255,0.06)" },
  consumido: { label: "Finalizado", color: "#8d90a0", bg: "rgba(255,255,255,0.06)" },
  revogado: { label: "Revogado", color: "#ffb4ab", bg: "rgba(239,68,68,0.12)" },
};

const overlayHeader = {
  borderBottom: "1px solid rgba(255,255,255,0.06)",
  paddingTop: "max(14px, env(safe-area-inset-top))",
};

const fetchTimeout = (url, opts = {}, ms = 30000) => {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  return fetch(url, { ...opts, signal: ctrl.signal }).finally(() => clearTimeout(timer));
};

export default function MobileConvites({ origem, eql, cpf, onClose, onAbrirPerfil }) {
  const [modo, setModo] = useState("checkin");
  const [condoNome, setCondoNome] = useState("");
  const [dados, setDados] = useState(null);
  const [semEmail, setSemEmail] = useState(false);
  const [falha, setFalha] = useState(false);
  const [form, setForm] = useState({
    nome: "", ddi: "+55", fone: "", cpf: "", email: "", tipo: "prestador",
    data_inicial: hojeISO(), data_final: hojeISO(), hora_inicial: agoraHHMM(), hora_final: "23:59",
    placa: "", modelo: "", cor: "",
  });
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [revogando, setRevogando] = useState(null);
  const [processandoRev, setProcessandoRev] = useState(false);
  const [zapModal, setZapModal] = useState(null);
  const [mailModal, setMailModal] = useState(null);
  const [ajudaAberta, setAjudaAberta] = useState(false);
  const [novoOpen, setNovoOpen] = useState(false);
  const [veiculoOpen, setVeiculoOpen] = useState(false);
  const [cpfErro, setCpfErro] = useState(false);
  const [emailErro, setEmailErro] = useState(false);
  const [emailOk, setEmailOk] = useState(true);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const carregar = useCallback(async () => {
    setFalha(false);
    try {
      const [rc, rl] = await Promise.all([
        fetchTimeout(`${API}/public/chave-virtual/config?origem=${encodeURIComponent(origem)}`),
        fetchTimeout(`${API}/public/chave-virtual/convites?origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(eql)}&cpf=${encodeURIComponent(cpf)}`),
      ]);
      const dc = await rc.json();
      if (dc.ok) { setModo(dc.modo || "checkin"); setCondoNome(dc.nome || ""); setEmailOk(dc.email_ok !== false); }
      const dl = await rl.json();
      if (dl.ok) { setDados(dl); setSemEmail(false); }
      else if (dl.motivo === "sem_email_morador") { setSemEmail(true); setDados({ convites: [], total: 0 }); }
      else setFalha(true);
    } catch { setFalha(true); }
  }, [origem, eql, cpf]);

  useEffect(() => { carregar(); }, [carregar]);

  const criar = async (e) => {
    e.preventDefault();
    if (enviando) return;
    const nome = form.nome.trim();
    const cpfDigits = form.cpf.replace(/\D/g, "");
    const foneDigits = form.fone.replace(/\D/g, "");
    const emailV = form.email.trim();
    if (nome.length < 2) { toast.error("Informe o nome do visitante."); return; }
    if (modo === "checkin") {
      if (cpfDigits.length !== 11) { toast.error("Informe o CPF do visitante (11 dígitos)."); return; }
      if (!cpfValido(cpfDigits)) { setCpfErro(true); toast.error("CPF do visitante inválido. Confira os dígitos."); return; }
    }
    if (foneDigits && foneDigits.length < 10) { toast.error("Telefone incompleto. Informe DDD + número."); return; }
    if (emailV && !/.+@.+\..+/.test(emailV)) { setEmailErro(true); toast.error("E-mail do visitante inválido."); return; }
    if (form.data_final < form.data_inicial) { toast.error("Data final anterior à inicial."); return; }
    setEnviando(true);
    try {
      const r = await fetchTimeout(`${API}/public/chave-virtual/convite`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          origem, eql, cpf, visitante_nome: nome,
          visitante_celular: foneDigits ? `${form.ddi.replace(/\D/g, "")}${foneDigits}` : undefined,
          visitante_cpf: cpfDigits || undefined,
          visitante_email: form.email.trim() || undefined,
          data_inicial: form.data_inicial, data_final: form.data_final,
          hora_inicial: form.hora_inicial || undefined,
          hora_final: form.hora_final || undefined,
          tipo: form.tipo,
          visitante_placa: form.placa.replace(/[^a-zA-Z0-9]/g, "").toUpperCase() || undefined,
          visitante_modelo: form.modelo.trim() || undefined,
          visitante_cor: form.cor.trim() || undefined,
        }),
      });
      const d = await r.json();
      if (d.ok) {
        setResultado({ ...d, _snap: {
          visitante_nome: nome, modo,
          data_inicial: form.data_inicial, hora_inicial: form.hora_inicial,
          data_final: form.data_final, hora_final: form.hora_final,
          link_pwa: d.link_pwa,
          visitante_celular: foneDigits ? `${form.ddi.replace(/\D/g, "")}${foneDigits}` : "",
          visitante_email: emailV,
          visitante_placa: form.placa.replace(/[^a-zA-Z0-9]/g, "").toUpperCase(),
          visitante_modelo: form.modelo.trim(),
          visitante_cor: form.cor.trim(),
        } });
        setForm({ nome: "", ddi: "+55", fone: "", cpf: "", email: "", tipo: "prestador",
          data_inicial: hojeISO(), data_final: hojeISO(), hora_inicial: agoraHHMM(), hora_final: "23:59",
          placa: "", modelo: "", cor: "" });
        setCpfErro(false); setEmailErro(false);
        setNovoOpen(false); setVeiculoOpen(false);
        carregar();
        window.scrollTo(0, 0);
      } else if (d.motivo === "sem_email_morador") setSemEmail(true);
      else if (d.motivo === "cpf_visitante_obrigatorio") toast.error("CPF do visitante é obrigatório neste condomínio.");
      else if (d.motivo === "dados_invalidos") toast.error("Dados inválidos. Confira as informações.");
      else toast.error("Não foi possível criar o convite. Tente novamente.");
    } catch { toast.error("Falha de comunicação. Tente novamente."); }
    finally { setEnviando(false); }
  };

  const confirmarRevogar = async () => {
    if (!revogando || processandoRev) return;
    setProcessandoRev(true);
    try {
      const r = await fetchTimeout(`${API}/public/chave-virtual/convite/${encodeURIComponent(revogando.id)}/revogar`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origem, eql, cpf }),
      });
      const d = await r.json();
      if (d.ok) { toast.success("Convite revogado."); setRevogando(null); carregar(); }
      else toast.error("Não foi possível revogar o convite.");
    } catch { toast.error("Falha de comunicação. Tente novamente."); }
    finally { setProcessandoRev(false); }
  };

  const copiarConvite = async (c) => {
    try {
      await navigator.clipboard.writeText(textoConvite(c, condoNome, eql));
      toast.success("Convite copiado. Cole no WhatsApp ou em qualquer app de mensagens.");
    } catch { toast.error("Não foi possível copiar."); }
  };

  const enviarWhatsApp = (c) => {
    const cel = (c.visitante_celular || "").replace(/\D/g, "");
    if (cel.length >= 10) window.open(zapUrl(cel, c, condoNome, eql), "_blank");
    else setZapModal({ convite: c, ddi: "+55", fone: "" });
  };

  const confirmarZap = () => {
    const ddi = (zapModal?.ddi || "").replace(/\D/g, "") || "55";
    const fone = (zapModal?.fone || "").replace(/\D/g, "");
    if (fone.length < 10) { toast.error("Informe DDD + celular do visitante."); return; }
    window.open(zapUrl(`${ddi}${fone}`, zapModal.convite, condoNome, eql), "_blank");
    setZapModal(null);
  };

  const enviarEmail = (c) => {
    const em = (c.visitante_email || "").trim();
    if (em) window.location.href = mailtoLink(em, c, condoNome, eql);
    else setMailModal({ convite: c, email: "" });
  };

  const confirmarMail = () => {
    const em = (mailModal?.email || "").trim();
    if (!/.+@.+\..+/.test(em)) { toast.error("Informe um e-mail válido."); return; }
    window.location.href = mailtoLink(em, mailModal.convite, condoNome, eql);
    setMailModal(null);
  };

  const convites = dados?.convites || [];
  const conviteResultado = resultado?._snap || null;

  return (
    <div className="fixed inset-0 z-40 flex flex-col" style={{ background: "#11131b", fontFamily: FONT_MAIN }}
      data-testid="appmobile-convites-overlay">
      <header className="flex items-center gap-3 px-4 py-3.5" style={overlayHeader}>
        <button type="button" data-testid="appmobile-convites-voltar" aria-label="Voltar" onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="flex-1 text-base font-bold" style={{ color: "#b4c5ff" }}
          data-testid="appmobile-convites-titulo">Convites</h1>
        <button type="button" data-testid="appmobile-convites-ajuda" aria-label="Ajuda"
          onClick={() => setAjudaAberta(true)}
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
          <HelpCircle className="h-4 w-4" />
        </button>
        <button type="button" data-testid="appmobile-convites-atualizar" aria-label="Atualizar"
          onClick={carregar}
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
          <RefreshCw className="h-4 w-4" />
        </button>
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-4 pb-28">
        {!dados && !falha && (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#8d90a0" }} />
          </div>
        )}
        {falha && (
          <div className="flex flex-col items-center gap-3 py-16" data-testid="appmobile-convites-erro">
            <p className="text-center text-xs" style={{ color: "#8d90a0" }}>Não foi possível carregar os convites.</p>
            <button type="button" data-testid="appmobile-convites-tentar-novamente" onClick={carregar}
              className="rounded-xl px-4 py-2.5 text-xs font-semibold text-white" style={{ background: "#2563EB" }}>
              Tentar novamente
            </button>
          </div>
        )}

        {dados && semEmail && (
          <div className="rounded-[14px] p-5 text-center" style={glassCard} data-testid="appmobile-convites-sem-email">
            <MailWarning className="h-10 w-10 mx-auto" style={{ color: "#f4a261" }} />
            <h2 className="mt-3 text-sm font-bold" style={{ color: "#c3c6d7" }}>Cadastre seu e-mail para usar Convites</h2>
            <p className="mt-2 text-xs leading-relaxed" style={{ color: "#8d90a0" }}>
              Por segurança e LGPD, você recebe um e-mail a cada acesso do seu visitante
              (com opção de revogar). Para criar convites, cadastre um e-mail válido no seu perfil.
            </p>
            <button type="button" data-testid="appmobile-convites-ir-perfil" onClick={onAbrirPerfil}
              className="mt-4 w-full rounded-xl py-3 text-sm font-semibold text-white" style={{ background: "#2563EB" }}>
              Ir para Meu Perfil
            </button>
          </div>
        )}

        {dados && !semEmail && (
          <>
            {!emailOk && (
              <div className="rounded-[14px] p-3 mb-4 flex items-start gap-2"
                style={{ ...glassCard, border: "1px solid rgba(244,162,97,0.4)" }}
                data-testid="appmobile-convites-email-off">
                <MailWarning className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "#f4a261" }} />
                <p className="text-[11px] leading-relaxed" style={{ color: "#f4a261" }}>
                  Envio de convite por e-mail indisponível no momento (configuração do sistema pendente).
                  Use WhatsApp ou Copiar link.
                </p>
              </div>
            )}
            {resultado && (
              <div className="rounded-[14px] p-4 mb-4" style={{ ...glassCard, border: "1px solid rgba(34,197,94,0.35)" }}
                data-testid="appmobile-convites-resultado">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" style={{ color: "#7ef0a0" }} />
                  <p className="text-sm font-bold" style={{ color: "#7ef0a0" }}>Convite criado!</p>
                  {resultado.email_enviado && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(34,197,94,0.12)", color: "#7ef0a0" }}
                      data-testid="appmobile-convites-email-enviado">Enviado por e-mail</span>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-2 rounded-xl px-3 py-2.5"
                  style={{ background: "rgba(255,255,255,0.04)" }}>
                  <Link2 className="h-4 w-4 shrink-0" style={{ color: "#8ab4ff" }} />
                  <span className="text-[11px] truncate flex-1" style={{ color: "#c3c6d7" }}
                    data-testid="appmobile-convites-link">{resultado.link_pwa}</span>
                </div>
                <div className="flex gap-2 mt-3">
                  <button type="button" data-testid="appmobile-convites-whatsapp"
                    onClick={() => enviarWhatsApp(conviteResultado)}
                    className="flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-xs font-semibold text-white"
                    style={{ background: "#22c55e" }}>
                    <Send className="h-3.5 w-3.5" /> WhatsApp
                  </button>
                  <button type="button" data-testid="appmobile-convites-copiar"
                    onClick={() => copiarConvite(conviteResultado)}
                    className="flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-xs font-semibold text-white"
                    style={{ background: "#2563EB" }}>
                    <Copy className="h-3.5 w-3.5" /> Copiar link
                  </button>
                  <button type="button" data-testid="appmobile-convites-outro"
                    onClick={() => { setResultado(null); setNovoOpen(true); set("nome", ""); set("ddi", "+55"); set("fone", ""); set("cpf", ""); set("email", ""); set("placa", ""); set("modelo", ""); set("cor", ""); setCpfErro(false); setEmailErro(false); setVeiculoOpen(false); set("hora_inicial", agoraHHMM()); set("hora_final", "23:59"); }}
                    className="rounded-xl px-3 py-2.5 text-xs font-medium"
                    style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)" }}>
                    Novo
                  </button>
                </div>
              </div>
            )}

            <div className="rounded-[14px] overflow-hidden" style={glassCard}
              data-testid="appmobile-convites-novo-collapsible" data-open={novoOpen}>
              <button type="button" data-testid="appmobile-convites-novo-toggle"
                onClick={() => { const abrir = !novoOpen; setNovoOpen(abrir); if (abrir) setResultado(null); }}
                className="w-full flex items-center gap-2 px-4 py-3.5 text-left"
                style={{ touchAction: "manipulation" }}>
                <Send className="h-4 w-4 shrink-0" style={{ color: "#b4c5ff" }} />
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "#c3c6d7" }}>Novo convite</span>
                <span className="flex-1" />
                <span className="text-[10px] px-2 py-0.5 rounded-full shrink-0"
                  style={{ background: "rgba(244,162,97,0.14)", color: "#f4a261" }}
                  data-testid="appmobile-convites-modo">
                  {modo === "checkin" ? "Modo: CPF obrigatório (check-in)" : "Modo: abertura automática"}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200"
                  style={{ color: "#8d90a0", transform: novoOpen ? "rotate(180deg)" : "none" }} />
              </button>
              {novoOpen && (
            <form onSubmit={criar} className="px-4 pb-4 space-y-3"
              data-testid="appmobile-convites-form">
              <input data-testid="appmobile-convites-nome" className={inputClass} style={inputStyle}
                placeholder="Nome do visitante *" value={form.nome} onChange={(e) => set("nome", e.target.value)} />
              <div className="flex gap-2">
                <input data-testid="appmobile-convites-ddi" className={inputClass}
                  style={{ ...inputStyle, width: "4.6rem", flexShrink: 0, textAlign: "center" }}
                  placeholder="+55" inputMode="tel" value={form.ddi}
                  onChange={(e) => set("ddi", fmtDDI(e.target.value))} />
                <input data-testid="appmobile-convites-celular" className={inputClass} style={inputStyle}
                  placeholder="(11) 99999-9999 — WhatsApp (opcional)" inputMode="numeric"
                  value={form.fone} onChange={(e) => set("fone", fmtFoneBR(e.target.value))} />
              </div>
              {modo === "checkin" && (
                <div>
                  <input data-testid="appmobile-convites-cpf" className={inputClass}
                    style={cpfErro ? { ...inputStyle, border: "1px solid #ef4444" } : inputStyle}
                    placeholder="CPF do visitante *" inputMode="numeric"
                    value={form.cpf}
                    onChange={(e) => { set("cpf", e.target.value); setCpfErro(false); }}
                    onBlur={() => setCpfErro(!!form.cpf && !cpfValido(form.cpf))} />
                  {cpfErro && (
                    <p className="text-[10px] mt-1" style={{ color: "#ffb4ab" }}
                      data-testid="appmobile-convites-cpf-erro">CPF inválido. Confira os dígitos.</p>
                  )}
                </div>
              )}
              <div>
                <input data-testid="appmobile-convites-email" className={inputClass}
                  style={emailErro ? { ...inputStyle, border: "1px solid #ef4444" } : inputStyle}
                  placeholder="E-mail do visitante — opcional" type="email"
                  value={form.email}
                  onChange={(e) => { set("email", e.target.value); setEmailErro(false); }}
                  onBlur={() => setEmailErro(!!form.email.trim() && !/.+@.+\..+/.test(form.email.trim()))} />
                {emailErro && (
                  <p className="text-[10px] mt-1" style={{ color: "#ffb4ab" }}
                    data-testid="appmobile-convites-email-erro">E-mail inválido. Confira o formato.</p>
                )}
                <p className="text-[10px] mt-1" style={{ color: "#8d90a0" }}>
                  Em branco, o visitante não recebe o convite por e-mail — envie pelo WhatsApp ou copie o link.
                </p>
              </div>

              <div className="rounded-xl overflow-hidden"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
                data-testid="appmobile-convites-veiculo-collapsible" data-open={veiculoOpen}>
                <button type="button" data-testid="appmobile-convites-veiculo-toggle"
                  onClick={() => setVeiculoOpen((o) => !o)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
                  style={{ touchAction: "manipulation" }}>
                  <Car className="h-4 w-4 shrink-0" style={{ color: "#b4c5ff" }} />
                  <span className="flex-1 text-xs font-semibold" style={{ color: "#c3c6d7" }}>Veículo (opcional)</span>
                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200"
                    style={{ color: "#8d90a0", transform: veiculoOpen ? "rotate(180deg)" : "none" }} />
                </button>
                {veiculoOpen && (
                  <div className="px-3 pb-3 space-y-2.5">
                    <input data-testid="appmobile-convites-placa" className={inputClass} style={inputStyle}
                      placeholder="Placa (ex.: ABC1D23)" maxLength={7}
                      value={form.placa}
                      onChange={(e) => set("placa", e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase())} />
                    <input data-testid="appmobile-convites-modelo" className={inputClass} style={inputStyle}
                      placeholder="Modelo (ex.: Onix)" value={form.modelo}
                      onChange={(e) => set("modelo", e.target.value)} />
                    <input data-testid="appmobile-convites-cor" className={inputClass} style={inputStyle}
                      placeholder="Cor (ex.: Prata)" value={form.cor}
                      onChange={(e) => set("cor", e.target.value)} />
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs mb-2" style={{ color: "#8d90a0" }}>Tipo de acesso *</p>
                <div className="grid grid-cols-2 gap-3">
                  {[["prestador", "Prestador", "Só 1 entrada e saída"],
                    ["hospede", "Hóspede", "Acesso livre na validade"]].map(([v, rot, sub]) => (
                    <button type="button" key={v} data-testid={`appmobile-convites-tipo-${v}`}
                      onClick={() => set("tipo", v)} className="rounded-xl p-3 text-left transition-all"
                      style={form.tipo === v
                        ? { background: "rgba(37,99,235,0.18)", border: "1.5px solid #2563EB" }
                        : { background: "rgba(255,255,255,0.03)", border: "1.5px solid rgba(255,255,255,0.08)" }}>
                      <span className="block text-sm font-semibold"
                        style={{ color: form.tipo === v ? "#b4c5ff" : "#c3c6d7" }}>{rot}</span>
                      <span className="block text-[11px] mt-0.5" style={{ color: "#8d90a0" }}>{sub}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] mb-1" style={{ color: "#8d90a0" }}>Data inicial *</p>
                  <input type="date" data-testid="appmobile-convites-data-inicial" className={inputClass}
                    style={{ ...inputStyle, colorScheme: "dark" }} value={form.data_inicial}
                    onChange={(e) => set("data_inicial", e.target.value)} />
                </div>
                <div>
                  <p className="text-[10px] mb-1" style={{ color: "#8d90a0" }}>Data final *</p>
                  <input type="date" data-testid="appmobile-convites-data-final" className={inputClass}
                    style={{ ...inputStyle, colorScheme: "dark" }} value={form.data_final}
                    onChange={(e) => set("data_final", e.target.value)} />
                </div>
                <div>
                  <p className="text-[10px] mb-1" style={{ color: "#8d90a0" }}>Hora inicial (opcional)</p>
                  <input type="time" data-testid="appmobile-convites-hora-inicial" className={inputClass}
                    style={{ ...inputStyle, colorScheme: "dark" }} value={form.hora_inicial}
                    onChange={(e) => set("hora_inicial", e.target.value)} />
                </div>
                <div>
                  <p className="text-[10px] mb-1" style={{ color: "#8d90a0" }}>Hora final (opcional)</p>
                  <input type="time" data-testid="appmobile-convites-hora-final" className={inputClass}
                    style={{ ...inputStyle, colorScheme: "dark" }} value={form.hora_final}
                    onChange={(e) => set("hora_final", e.target.value)} />
                </div>
              </div>

              <button type="submit" data-testid="appmobile-convites-submit" disabled={enviando}
                className="w-full rounded-xl py-3.5 text-sm font-semibold text-white transition-opacity"
                style={{ background: "#2563EB", opacity: enviando ? 0.6 : 1 }}>
                {enviando ? "Criando convite..." : "Criar convite"}
              </button>
            </form>
              )}
            </div>

            <p className="text-xs font-bold uppercase tracking-wider mt-6 mb-2" style={{ color: "#8d90a0" }}>
              Meus convites
            </p>
            {convites.length === 0 ? (
              <div className="rounded-[14px] p-6 text-center" style={glassCard}
                data-testid="appmobile-convites-vazio">
                <p className="text-xs" style={{ color: "#8d90a0" }}>Nenhum convite criado ainda.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="appmobile-convites-lista">
                {convites.map((c) => {
                  const st = STATUS_PILL[c.status] || { label: c.status || "—", color: "#8d90a0", bg: "rgba(255,255,255,0.06)" };
                  return (
                    <div key={c.id} className="rounded-[14px] p-3" style={glassCard}
                      data-testid={`appmobile-convites-item-${c.id}`}>
                      <div className="flex items-center gap-2">
                        <p className="flex-1 text-sm font-semibold truncate" style={{ color: "#c3c6d7" }}>
                          {c.visitante_nome}
                        </p>
                        <span className="text-[10px] px-2 py-0.5 rounded-full shrink-0"
                          style={{ background: "rgba(180,197,255,0.1)", color: "#8ab4ff" }}>
                          {c.visitavip === 1 ? "Hóspede" : "Prestador"}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full shrink-0"
                          style={{ background: st.bg, color: st.color }}
                          data-testid={`appmobile-convites-status-${c.id}`}>{st.label}</span>
                      </div>
                      <p className="text-[11px] mt-1.5" style={{ color: "#8d90a0" }}>
                        {fmtData(c.data_inicial)}{c.hora_inicial ? ` ${c.hora_inicial}` : ""}
                        {" → "}{fmtData(c.data_final)}{c.hora_final ? ` ${c.hora_final}` : ""}
                        {" · "}Usos: {c.usos_realizados ?? 0}{c.max_usos ? `/${c.max_usos}` : ""}
                      </p>
                      {c.status === "ativo" && c.link_pwa && (
                        <div className="flex gap-2 mt-2" data-testid={`appmobile-convites-share-${c.id}`}>
                          <button type="button" data-testid={`appmobile-convites-copiar-${c.id}`}
                            onClick={() => copiarConvite(c)}
                            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold"
                            style={{ background: "rgba(180,197,255,0.1)", color: "#8ab4ff" }}>
                            <Copy className="h-3 w-3" /> Copiar
                          </button>
                          <button type="button" data-testid={`appmobile-convites-zap-${c.id}`}
                            onClick={() => enviarWhatsApp(c)}
                            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold"
                            style={{ background: "rgba(34,197,94,0.12)", color: "#7ef0a0" }}>
                            <MessageCircle className="h-3 w-3" /> WhatsApp
                          </button>
                          <button type="button" data-testid={`appmobile-convites-mail-${c.id}`}
                            onClick={() => enviarEmail(c)}
                            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold"
                            style={{ background: "rgba(244,162,97,0.12)", color: "#f4a261" }}>
                            <Mail className="h-3 w-3" /> E-mail
                          </button>
                        </div>
                      )}
                      {c.status === "ativo" && (
                        <button type="button" data-testid={`appmobile-convites-revogar-${c.id}`}
                          onClick={() => setRevogando(c)}
                          className="mt-2 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold"
                          style={{ background: "rgba(239,68,68,0.12)", color: "#ffb4ab" }}>
                          <Ban className="h-3 w-3" /> Revogar
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      {revogando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6"
          data-testid="appmobile-convites-revogar-modal">
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)" }}
            onClick={() => !processandoRev && setRevogando(null)} />
          <div className="relative w-full max-w-sm rounded-[14px] p-5" style={glassCard}>
            <p className="text-sm font-bold" style={{ color: "#c3c6d7" }}>Revogar convite</p>
            <p className="text-xs mt-2 leading-relaxed" style={{ color: "#8d90a0" }}>
              Revogar o convite de <b style={{ color: "#c3c6d7" }}>{revogando.visitante_nome}</b>?
              O link para de funcionar imediatamente.
            </p>
            <div className="flex gap-3 mt-5">
              <button type="button" data-testid="appmobile-convites-revogar-cancelar"
                onClick={() => setRevogando(null)} disabled={processandoRev}
                className="flex-1 py-3 rounded-xl text-sm font-medium"
                style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)" }}>
                Cancelar
              </button>
              <button type="button" data-testid="appmobile-convites-revogar-confirmar"
                onClick={confirmarRevogar} disabled={processandoRev}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white"
                style={{ background: "#dc2626", opacity: processandoRev ? 0.6 : 1 }}>
                {processandoRev ? "Revogando..." : "Sim, revogar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {ajudaAberta && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6"
          data-testid="appmobile-convites-ajuda-modal">
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)" }}
            onClick={() => setAjudaAberta(false)} />
          <div className="relative w-full max-w-sm rounded-[14px] p-5" style={glassCard}>
            <button type="button" data-testid="appmobile-convites-ajuda-fechar" aria-label="Fechar"
              onClick={() => setAjudaAberta(false)}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-lg active:bg-white/10"
              style={{ color: "#8d90a0", touchAction: "manipulation" }}>
              <X className="h-4 w-4" />
            </button>
            <p className="text-sm font-bold pr-8" style={{ color: "#c3c6d7" }}>Ajuda sobre Convites</p>
            <p className="text-xs mt-3 leading-relaxed" style={{ color: "#8d90a0" }}>
              Nesta opção é possível cadastrar um visitante para acesso rápido, enviando um link de convite de acesso, com data e hora para determinar o tempo de permanência e ainda informar os dados do veículo como placa, modelo e cor.
            </p>
            <p className="text-xs mt-4 font-bold" style={{ color: "#c3c6d7" }}>Informação Importante</p>
            <p className="text-xs mt-2 leading-relaxed" style={{ color: "#8d90a0" }}>
              Todo o processo de cadastro e liberações são documentados por e-mail, uma vez que o visitante estará sob responsabilidade do Proprietário que fez o cadastro ou do seu preposto Autorizado a partir do cadastro efetuado.
            </p>
            <p className="text-xs mt-2 leading-relaxed" style={{ color: "#8d90a0" }}>
              Em caso de dúvidas, entre em contato com a administração ou síndico do empreendimento.
            </p>
          </div>
        </div>
      )}

      {zapModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6"
          data-testid="appmobile-convites-zap-modal">
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)" }}
            onClick={() => setZapModal(null)} />
          <div className="relative w-full max-w-sm rounded-[14px] p-5" style={glassCard}>
            <p className="text-sm font-bold" style={{ color: "#c3c6d7" }}>Enviar pelo WhatsApp</p>
            <p className="text-xs mt-2 leading-relaxed" style={{ color: "#8d90a0" }}>
              Informe o celular do visitante com DDD. O WhatsApp abre com o convite pronto para envio.
            </p>
            <div className="flex gap-2 mt-3">
              <input data-testid="appmobile-convites-zap-ddi" className={inputClass}
                style={{ ...inputStyle, width: "4.6rem", flexShrink: 0, textAlign: "center" }}
                placeholder="+55" inputMode="tel" value={zapModal.ddi}
                onChange={(e) => setZapModal((m) => ({ ...m, ddi: fmtDDI(e.target.value) }))} />
              <input data-testid="appmobile-convites-zap-fone" className={inputClass} style={inputStyle}
                placeholder="(11) 99999-9999" inputMode="numeric" value={zapModal.fone}
                onChange={(e) => setZapModal((m) => ({ ...m, fone: fmtFoneBR(e.target.value) }))} />
            </div>
            <div className="flex gap-3 mt-4">
              <button type="button" data-testid="appmobile-convites-zap-cancelar"
                onClick={() => setZapModal(null)}
                className="flex-1 py-3 rounded-xl text-sm font-medium"
                style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)" }}>
                Cancelar
              </button>
              <button type="button" data-testid="appmobile-convites-zap-enviar" onClick={confirmarZap}
                className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl text-sm font-semibold text-white"
                style={{ background: "#22c55e" }}>
                <MessageCircle className="h-4 w-4" /> Enviar
              </button>
            </div>
          </div>
        </div>
      )}

      {mailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6"
          data-testid="appmobile-convites-mail-modal">
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)" }}
            onClick={() => setMailModal(null)} />
          <div className="relative w-full max-w-sm rounded-[14px] p-5" style={glassCard}>
            <p className="text-sm font-bold" style={{ color: "#c3c6d7" }}>Enviar por e-mail</p>
            <p className="text-xs mt-2 leading-relaxed" style={{ color: "#8d90a0" }}>
              Informe o e-mail do visitante. Seu aplicativo de e-mail abre com o convite pronto para envio.
            </p>
            <input data-testid="appmobile-convites-mail-email" className={`${inputClass} mt-3`} style={inputStyle}
              placeholder="email@exemplo.com" type="email"
              value={mailModal.email}
              onChange={(e) => setMailModal((m) => ({ ...m, email: e.target.value }))} />
            <div className="flex gap-3 mt-4">
              <button type="button" data-testid="appmobile-convites-mail-cancelar"
                onClick={() => setMailModal(null)}
                className="flex-1 py-3 rounded-xl text-sm font-medium"
                style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)" }}>
                Cancelar
              </button>
              <button type="button" data-testid="appmobile-convites-mail-enviar" onClick={confirmarMail}
                className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl text-sm font-semibold text-white"
                style={{ background: "#2563EB" }}>
                <Mail className="h-4 w-4" /> Enviar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
